/**
 * QR-3: чат участника, вошедшего по приглашению, ПРЯМО В БРАУЗЕРЕ.
 * Публичный маршрут `/j/chat` — вне PrivateRoutes: у участника нет учётной
 * записи, сессии ЛК и быть не должно.
 *
 * Зачем это есть: приглашённый может оказаться на любом устройстве — iPhone,
 * чужой ноутбук, планшет, — а нативного клиента под них нет и не будет к
 * сроку. Криптослой, контроллер и стор здесь ТЕ ЖЕ, что у владельца
 * (ChatController + olmClient): отдельной, более слабой реализации шифрования
 * для «гостей» не заводим — это ровно тот путь, которым мессенджеры теряют
 * сквозное шифрование.
 *
 * Отличия от страницы ЛК:
 * - принципал `member` и member-JWT вместо сессии владельца (memberTransport);
 * - did берётся ИЗ СЕССИИ, а не из browserDeviceId(): сервер положил его
 *   клеймом в токен при join, и разъехаться они не имеют права;
 * - визави не выбирается (сервер знает пару участника), ensure идёт пустым.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ChatController, PEER_WAIT_RETRY_MS } from '../../msg/chatController';
import { RoomController } from '../../msg/roomController';
import { MsgApi } from '../../msg/msgApi';
import PushToggle from '../../msg/pushToggle';
import { createMemberTransport } from '../../msg/memberTransport';
import {
  clearMemberSession, forgetMemberSession, loadMemberSession, renameStoredSession,
  saveRefreshedSession, sessionIsPersisted, parseMemberSession, MEMBER_SESSION_KEY,
} from '../../msg/memberSession';
import {
  askYield, canAskYield, onYieldRequest, requestLeadership, setLocalHandoff,
  TAKEOVER_WAIT_MS, waitLocalHandoff,
} from '../../msg/leader';
import { CABINET_PATH, LANDING_PATH } from '../../msg/inviteLink';
import { takeArrivalIntent } from '../../msg/arrivalIntent';
import { knownSenderName, senderName } from '../../msg/roomNames';
import { AttachmentDraft, MsgAttachments, UploadingNote } from '../../msg/attachmentsUi';
import { replySnapshot } from '../../msg/replyTo';
import {
  indexMessages, ReplyButton, ReplyDraftBar, ReplyQuote, useQuoteJump,
} from '../../msg/replyToUi';
import PinnedWarnings, { SendBlocked } from '../../msg/pinnedWarnings';
import SendButton from '../../msg/sendButton';
import SecurityPanel, { SecurityLock, unverifiedCount } from '../../msg/securityPanel';
import DeviceTransferPanel from '../../msg/deviceTransferPanel';
import DeviceImportPanel from '../../msg/deviceImportPanel';
import { dropCryptoStore, exportDevice, wipeDevice } from '../../msg/deviceTransfer';
import { refreshMemberToken } from '../../msg/publicApi';
// Д-17: плашка «вышло обновление» — у участника каркаса кабинета нет, поэтому
// своё место монтирования: в шапке карточки, вне прокрутки ленты.
import UpdateBanner from '../../msg/updateBanner';
import '../Msg/msgChat.css';
import './memberChat.css';

/**
 * Передача лидерства ВНУТРИ вкладки — тот же паттерн, что в msgChat (ревью
 * QR-1b р.3, High), и понадобился он здесь ровно тогда, когда появился
 * автоповтор ожидания визави (ревью Codex 25.08, High-1).
 *
 * 🔴 Почему без него ломается: `requestLeadership` просит Web Lock с
 * `ifAvailable`, а cleanup отпускает старый лок только ПОСЛЕ асинхронного
 * stop() контроллера. На перезапуске эффекта новый заход спросил бы лок
 * раньше — и получил честный отказ от СОБСТВЕННОГО, ещё не отпущенного
 * лока: гость, которому всего-то надо дождаться владельца, вместо ожидания
 * увидел бы «чат открыт в другой вкладке» и снова упёрся в перезаход.
 * Другая вкладка — другой модуль, для неё честный отказ остаётся.
 */
/* 🔴 QR-2 В2: эстафета переехала в `msg/leader` — замок один на origin, а
 * страниц msg-машины стало три (чат владельца, чат участника, комната).
 * Своя копия переменной вернула бы отказ от собственного лока на переходе
 * между ними. Реэкспорт оставлен: на него ссылаются тесты. */
export { waitLocalHandoff } from '../../msg/leader';

/** Как часто спрашиваем комнату, пока человек смотрит в личный чат (WEB-12). */
export const ROOM_POLL_MS = 30_000;

const TRUST_TEXT = {
  first_use: 'Первый контакт с этим устройством собеседника. Сверьте код безопасности при случае.',
  key_changed: 'Ключ устройства собеседника СМЕНИЛСЯ (например, переустановка). Сверьте код безопасности заново!',
};

/**
 * Перезагрузка после восстановления из копии — отдельной ссылкой ради тестов
 * (тот же приём, что у `UpdateBanner`): jsdom навигацию не умеет и роняет
 * «Not implemented», а проверять надо именно ЕЁ — без перезагрузки страница
 * осталась бы с мёртвым контроллером поверх воскресшего стора.
 */
const defaultReload = () => window.location.reload();

const MemberChat = ({ reload = defaultReload }) => {
  // Сессия читается ОДИН раз на mount: перечитывать её на каждый рендер
  // значило бы менять принципал под живым контроллером.
  const [session, setSession] = useState(() => loadMemberSession());
  const [chat, setChat] = useState(null);
  // WEB-23: панель «Защита переписки» — вход замком в шапке и пунктом в «Ещё».
  const [securityOpen, setSecurityOpen] = useState(false);
  const [myDevice, setMyDevice] = useState(null);
  // Фокус возвращается на замок при закрытии — иначе он падает на `body`, и
  // клавиатурой до замка надо добираться табом с начала страницы.
  const securityLockRef = useRef(null);
  const closeSecurity = () => {
    setSecurityOpen(false);
    securityLockRef.current?.focus();
  };
  // Д-3: панель «Копия и перенос» — вход пунктом в подвале «Ещё».
  const [transferOpen, setTransferOpen] = useState(false);
  // Снимок идёт при ОСТАНОВЛЕННОМ транспорте (дизайн §4): пока true, эффект
  // подъёма не поднимается, а его уборка уже погасила транспорт и контроллер.
  const [snapshotting, setSnapshotting] = useState(false);
  // Устройство погашено переносом — терминальное состояние, как `replaced`.
  const [moved, setMoved] = useState(false);
  // Комнату надо знать по id, а не по «она есть»: без него отбор не смог бы
  // положить в файл комнатную историю, а положить ЧУЖУЮ ему нельзя.
  const [roomId, setRoomId] = useState(null);
  const transferBtnRef = useRef(null);
  const closeTransfer = () => {
    setTransferOpen(false);
    transferBtnRef.current?.focus();
  };
  /**
   * Д-3 «хвост»: восстановление из копии ПРЯМО на сломанном устройстве.
   *
   * `repairing` — то же, что `snapshotting` и `moved`: пока true, эффект
   * подъёма не заводится, а его уборка уже погасила контроллер. Иначе живая
   * машина держала бы крипто-стор открытым, и запись из копии легла бы
   * поверх занятой базы (в лучшем случае), а `deleteDatabase` молча упёрся
   * бы в `blocked` — ровно то, что поймал живой прогон E3.
   */
  const [repairing, setRepairing] = useState(false);
  const [repairOpen, setRepairOpen] = useState(false);
  // Причина отказа, а не булев флаг: «закрыть другие вкладки» и «восстанавливать
  // нечего» лечатся по-разному, и первое лечится повтором.
  const [repairFailed, setRepairFailed] = useState(null);
  const repairBusyRef = useRef(false);
  const [blocked, setBlocked] = useState(false);
  const [lost, setLost] = useState(false);
  // Браузер запретил хранение (приватный режим Safari, «блокировать данные»):
  // сессия живёт только в этой вкладке — человек обязан знать, что закрывать
  // её нельзя, иначе он молча потеряет чат, а ссылка одноразовая.
  const [fragile, setFragile] = useState(() => !sessionIsPersisted());
  // В другой вкладке вошли ДРУГИМ участником (или вышли): эта вкладка обязана
  // погаснуть, а человеку нужно сказать, почему — иначе он будет смотреть в
  // «Вы ещё не вошли в чат» и решит, что чат сломался (ревью Codex р.4).
  const [replaced, setReplaced] = useState(false);
  const [input, setInput] = useState('');
  // ATT С2: выбранный файл-черновик. Живёт только в ЛИЧНОЙ переписке —
  // комната научится вложениям срезом С3 (fan-out ключа).
  const [file, setFile] = useState(null);
  // WEB-16: на какое сообщение отвечаем (пока только в личной переписке —
  // комната это срез 3).
  const [replyTo, setReplyTo] = useState(null);
  // Оригиналы для цитат — по ленте ТЕКУЩЕГО канала: `chat` меняется вместе
  // с видом, значит и карта, и цитаты не утекают между личным и комнатой.
  const byId = indexMessages(chat?.messages);
  const fileRef = useRef(null);
  // WEB-31: фокус возвращается в поле после отправки кнопкой
  const inputRef = useRef(null);
  // Собеседник ещё не поднимал крипту: ждём его и перезапускаем подъём сами.
  // Счётчик — в зависимостях эффекта контроллера: это честный перезапуск
  // (лидерство, транспорт, машина), а не попытка оживить мёртвый контроллер.
  const [retryTick, setRetryTick] = useState(0);
  // Перенос чата в эту вкладку (с.64) — на телефоне участника вкладку-держателя
  // найти так же нереально, как у владельца.
  const [takeTick, setTakeTick] = useState(0);
  const [yielded, setYielded] = useState(false);
  // 🔴 Ожидание переноса — НОМЕР попытки, а не булев флаг (ревью Codex р.1,
  // High-1): заход эффекта гасит спиннер, только если тот принадлежит ему.
  // Иначе «Переносим чат сюда…» переживало бы отменённый заход навсегда.
  const [takingTick, setTakingTick] = useState(0);
  const taking = takingTick > 0;
  const [takeFailed, setTakeFailed] = useState(false);
  const takingTickRef = useRef(0);
  const controllerRef = useRef(null);
  // Правка собственной подписи (07.09): опечатка при входе («Алнксей» на бою)
  // иначе жила бы навсегда — в ростере, в подписях реплик и у владельца.
  const [renamingSelf, setRenamingSelf] = useState(false);
  const [selfDraft, setSelfDraft] = useState('');
  const [selfError, setSelfError] = useState(null);
  const [savingSelf, setSavingSelf] = useState(false);
  /**
   * WEB-22 (Д-18): служебный подвал СВЁРНУТ, пока его не спросили.
   *
   * Замер 02.09 на iPhone SE во встроенном браузере мессенджера: ленте
   * доставалось 96 px из 470 (одна реплика из четырёх), а «подпись + выход»
   * съедали 154. Настройка, которой пользуются раз в жизни, не может стоить
   * трёх реплик переписки. Подпись остаётся на виду всегда — владелец
   * 09.09 не нашёл своей и просил её показать; прячется только то, что
   * ниже.
   */
  const [footOpen, setFootOpen] = useState(false);
  /**
   * WEB-22 (Д-19): «Выйти из чата» спрашивает подтверждение.
   *
   * До правки это была кнопка в один клик В ДВУХ САНТИМЕТРАХ от «Отправить»,
   * и на телефоне мы же сами растили ей цель до 40 px. А делает она
   * необратимое: стирает сессию — единственное доказательство, по которому
   * сервер узнаёт прежнего участника (Д-2), — оставляя did. Человек
   * возвращается по ссылке НОВЫМ участником: переписка недоступна, в ростере
   * владельца лишняя строка. Живой прогон 02.09: у одного участника три
   * новые строки за восемь минут.
   */
  const [leaveAsk, setLeaveAsk] = useState(false);
  const leaveCancelRef = useRef(null);
  const leaveOpenRef = useRef(null);
  const returnLeaveFocus = useRef(false);
  /**
   * 🔴 СВОЯ подпись — ОТДЕЛЬНОЕ состояние, а не поле `session`.
   *
   * `session` — это удостоверение вкладки: от него зависит эффект, и любая его
   * новая копия гасит транспорт и поднимает его заново. А токен внутри
   * состояния устаревает по построению (продление живёт в транспорте и в
   * хранилище, состояние оно намеренно не трогает). Клади подпись туда — и
   * смена имени перезапускала бы чат СТАРЫМ токеном (ревью Codex р.5,
   * Blocker). Имя — это поле экрана, ему нечего делать в удостоверении.
   */
  const [selfNameOwn, setSelfNameOwn] = useState(() => session?.displayName ?? null);
  // WEB-19 §4.1/§4.3 (Д-2): с чем человек пришёл с лендинга (продолжен как
  // прежний участник / пришёл менять подпись). Читается ОДИН раз при монтировании.
  const [arrival, setArrival] = useState(() => takeArrivalIntent());
  const selfInputRef = useRef(null);
  const selfOpenRef = useRef(null);
  const returnSelfFocus = useRef(false);
  // 🔴 Уступает только держатель: заблокированная вкладка чужую просьбу
  // игнорирует, иначе показала бы «перенесён» про чат, которого не держала.
  const heldRef = useRef(false);
  // 🔴 Транспорт держим отдельно от контроллера: выход обязан заглушить
  // продление РАНЬШЕ, чем вычистит хранилище (ревью Codex р.3) — иначе
  // ответ на летящий refresh запишет свежий токен обратно.
  const transportRef = useRef(null);
  // WEB-24: клиент для тумблера уведомлений.
  // 🔴 Транспорт читается в момент ВЫЗОВА, а не в момент мемоизации (ревью,
  // High): `transportRef.current` присваивается в эффекте, после нескольких
  // await, а useMemo считается в РЕНДЕРЕ — там всегда ещё null. Прежняя
  // редакция отдавала `new MsgApi(did, null)`, и `transport = ownerTransport`
  // по умолчанию не срабатывал (дефолт параметра ловит только undefined):
  // тумблер участника падал бы на первом же нажатии, объясняя это ложью
  // «уведомления на сервере не настроены», и после этого гас навсегда.
  const pushApi = useMemo(
    () => (session?.did ? new MsgApi(session.did, {
      request: (...args) => transportRef.current.request(...args),
      poke: (...args) => transportRef.current.poke(...args),
      streamToken: () => transportRef.current.streamToken(),
    }) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session?.did],
  );
  // Выход, случившийся ПОКА поднимается лидерство: транспорта ещё нет, гасить
  // нечего, а cleanup эффекта придёт позже — флаг закрывает это окно.
  const leavingRef = useRef(false);
  const listRef = useRef(null);
  // WEB-16 срез 4: переход к оригиналу по нажатию на цитату.
  const { jumpTo, highlighted, clearHighlight } = useQuoteJump(listRef);
  /**
   * QR-2 В2: что смотрит участник — личную переписку с владельцем или ОБЩУЮ
   * комнату. Решение владельца 01.09: видно и то и другое, личная линия к нему
   * сохраняется.
   *
   * 🔴 Одновременно живёт РОВНО ОДНА машина: замок `total-msg-leader` один на
   * origin, и две машины на одном Olm-сторе портят состояние. Поэтому
   * переключение — это перезапуск эффекта с другим контроллером, а не второй
   * контроллер рядом.
   */
  const [view, setView] = useState('direct');
  // Цитаты показываются в ОБОИХ каналах — но только когда снимок на
  // экране принадлежит выбранному. Переключение заводит новый контроллер,
  // а прежний снимок висит до его первого обновления: в эту щель кнопка
  // «ответить» стояла бы под чужим каналом (находка ревью среза 2).
  const quotesHere = chat?.view === view;
  /**
   * 🔴 Текущий вид ДЛЯ АСИНХРОННЫХ ХВОСТОВ. Поле ввода на этой странице ОДНО
   * на оба вида, поэтому отказ отправки, приехавший после переключения,
   * обязан знать, куда он вернулся бы: личный текст, подставленный в поле
   * ОБЩЕЙ комнаты, уходит всем (ревью 12.09, р.7, High). В замыкании `send`
   * живёт снимок `view` на момент клика — сравнивать надо с этим ref.
   */
  const viewRef = useRef(view);
  useEffect(() => { viewRef.current = view; }, [view]);
  // Вкладку комнаты показываем, только если комната ЕСТЬ: при выключенном
  // флаге её поверхности нет вовсе, и вкладка вела бы в «недоступно».
  const [hasRoom, setHasRoom] = useState(false);
  /**
   * WEB-12: сколько сообщений комнаты ждёт человека, пока он смотрит в личный
   * чат.
   *
   * 🔴 Зачем. 03.09 владелец написал в комнату двоим — копии дошли до всех
   * устройств, и НИ ОДИН участник за ними не пришёл. По базе видно почему:
   * курсоров у участников не было вовсе. По умолчанию открыт личный чат,
   * машина живёт одна на origin, и пока открыт он, комната не опрашивается
   * вообще: узнать о сообщении было НЕЧЕМ. Признак на вкладке — это не
   * украшение, без него комната не работает как канал связи.
   *
   * Число считает сервер по метаданным (`unread` в `GET /msg/rooms`) — цена
   * опроса одна строка, крипта не поднимается.
   */
  const [roomUnread, setRoomUnread] = useState(0);

  // Сосед просит отдать лидерство: гасим эффект, его cleanup останавливает
  // контроллер и лишь потом отпускает лок.
  useEffect(() => onYieldRequest(() => {
    if (heldRef.current) setYielded(true);
  }), []);

  /**
   * 🔴 ЕДИНСТВЕННОЕ место, знающее, что делать с исходом записи в
   * хранилище. Сюда ходят ОБА писателя — продление токена и смена подписи.
   * Пока смена подписи разбирала исход сама, она его попросту игнорировала и
   * оживляла вкладку, у которой слот уже отобрали (ревью Codex р.1, Blocker).
   *
   * `stale` — слот больше не наш: в соседней вкладке вошли другим участником
   * или вышли. Гасим вкладку ЗДЕСЬ, не дожидаясь storage-события (оно
   * асинхронное), иначе она продолжит ходить в сеть member-JWT отобранной
   * сессии.
   *
   * @returns {boolean} true — запись состоялась и вкладка живёт дальше.
   */
  const applyWriteOutcome = (outcome) => {
    if (outcome === 'fragile') setFragile(true);
    if (outcome === 'stale') {
      leavingRef.current = true;
      transportRef.current?.dispose?.();
      forgetMemberSession(); // память вкладки тоже не должна её воскрешать
      setReplaced(true);
      setSession(null);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!session || yielded || snapshotting || moved || repairing) return undefined;
    // Номер попытки переноса, которой принадлежит ЭТОТ заход (до первого await).
    const myTick = takeTick;
    const isTakeover = myTick > 0 && takingTickRef.current === myTick;
    const doneTaking = () => {
      if (takingTickRef.current === myTick) takingTickRef.current = 0;
      setTakingTick((cur) => (cur === myTick ? 0 : cur));
    };
    leavingRef.current = false;
    let cancelled = false;
    let roomPoll = null;
    let onVisible = null;
    let roomAsk = 0;          // номер опроса комнаты: применяем только последний
    let controller = null;
    let leader = null;
    let leaderP = null; // сам запрос лидерства — может висеть в момент ухода
    let transport = null;

    (async () => {
      // Прежний контроллер ЭТОЙ вкладки отпустил лок (перезапуск автоповтором)
      await waitLocalHandoff();
      if (cancelled || leavingRef.current) return;
      // Лидерство ДО подъёма крипты: две вкладки на одном IndexedDB-сторе
      // портят Olm-состояние (та же политика, что в ЛК).
      // После просьбы уступить ждём лок В ОЧЕРЕДИ: держатель отдаёт его не
      // мгновенно — stop() дожидается уже начатого шага машины.
      leaderP = requestLeadership(myTick > 0 ? { waitMs: TAKEOVER_WAIT_MS } : {});
      leader = await leaderP;
      // leavingRef, а не только cancelled: человек мог нажать «Выйти», пока
      // лидерство поднималось — cleanup эффекта придёт лишь на следующем
      // коммите, и без флага мы завели бы живой чат ПОСЛЕ выхода.
      // 🔴 Спиннер гасится на ОБОИХ путях, включая отменённый (High-1).
      doneTaking();
      if (cancelled || leavingRef.current) { leader?.release?.(); return; }
      if (!leader.granted) { setBlocked(true); setTakeFailed(isTakeover); return; }
      heldRef.current = true;

      const { did } = session;
      transport = createMemberTransport(session, {
        // 🔴 Не saveMemberSession: слот мог достаться другому участнику,
        // пока продление было в пути (ревью Codex р.6) — слепая запись
        // отобрала бы у него чат.
        onSession: (fresh) => { applyWriteOutcome(saveRefreshedSession(fresh)); },
        // Токен мёртв (приглашение отозвали, участника удалили) — честный
        // экран вместо вечного «подключение…»
        onLost: () => { if (!cancelled) setLost(true); },
      });
      // Выход мог случиться, пока поднималось лидерство: тогда транспорт
      // рождается уже закрытым и в сеть не ходит.
      if (cancelled || leavingRef.current) {
        transport.dispose(); leader?.release?.(); return;
      }
      transportRef.current = transport;
      const api = new MsgApi(did, transport);
      const principal = { type: 'member', id: session.memberId };
      // 🔴 Снимок ПОМЕЧАЕТСЯ каналом, из которого пришёл (находка ревью
      // WEB-16 среза 2, Medium). Переключение вида заводит новый контроллер,
      // но прежний `chat` живёт на экране, пока новый не выдаст первый
      // снимок: в эту щель кнопка «ответить» стояла под сообщениями КОМНАТЫ,
      // а выбранная цитата уезжала в личную переписку — с куском чужого
      // текста и подписью не того человека.
      const onUpdate = (snapshot) => setChat(
        snapshot ? { ...snapshot, view } : snapshot,
      );
      controller = view === 'room'
        ? new RoomController({ api, did, principal, onUpdate })
        : new ChatController({ api, did, principal, onUpdate });
      controllerRef.current = controller;
      // RoomController.start() визави не принимает — комнату он находит сам
      if (view === 'room') controller.start();
      else controller.start(null);
      // Есть ли вообще комната и сколько там ждёт — один дешёвый запрос тем
      // же транспортом.
      //
      // 🔴 Почему опрос живёт ЗДЕСЬ, под лидерством (ответ на находку ревью
      // р.1, High «polling не обязан зависеть от Web Lock»). Формально не
      // обязан — но ходит он ТЕМ ЖЕ member-транспортом, а второй транспорт в
      // той же вкладке завёл бы второй цикл продления одного и того же токена:
      // ровно та гонка, от которой страницу уже лечили (ответ на летящий
      // refresh пишет свежий токен в слот, который тем временем достался
      // другому участнику). Цена отказа при этом нулевая: вкладка-не-лидер
      // не показывает НИ ОДНОГО чата — там стоит «чат открыт в другой
      // вкладке», и переключателя с признаком в ней нет вовсе. Значок,
      // который некуда нажать, сигналом не является. Ошибку глотаем осознанно: 404 при выключенном флаге
      // это НЕ поломка, а «раздела нет», и участник должен увидеть ровно то
      // же, что видел вчера, — свой личный чат без лишних вкладок.
      const askRoom = () => {
        // 🔴 Номер запроса (ревью Codex р.1, Medium): опросов в полёте бывает
        // несколько — начальный, по интервалу и по возврату на вкладку. Без
        // номера медленный старый ответ приезжает ПОСЛЕ свежего и откатывает
        // значок с «1» на «0», то есть гасит сигнал ровно тогда, когда он
        // нужен. Применяем только последний.
        roomAsk += 1;
        const mine = roomAsk;
        return api.listRooms()
          .then((r) => {
            if (cancelled || mine !== roomAsk) return;
            const room = r?.rooms?.[0];
            setHasRoom(Boolean(room));
            setRoomId(room?.id ?? null);
            setRoomUnread(room?.unread ?? 0);
          })
          .catch((e) => {
            if (cancelled || mine !== roomAsk) return;
            // 🔴 Вкладку прячем ТОЛЬКО на приговорах (ревью Codex р.1, Low):
            // раздел выключили, доступ отобрали, участника удалили. Обрыв сети
            // приговором не является — на опросе раз в 30 секунд вкладка
            // «Общая комната» мигала бы у человека на каждом сбое связи.
            if ([401, 403, 404].includes(e?.status)) {
              setHasRoom(false);
              setRoomUnread(0);
            }
          });
      };
      askRoom();
      // 🔴 Повторяем, пока человек смотрит в ЛИЧНЫЙ чат: одного запроса на
      // загрузке хватило бы только тому, кто пришёл после сообщения. Тот, кто
      // уже сидит в переписке с владельцем, иначе так и не узнал бы — а это
      // ровно случай 03.09. На вкладке комнаты опрос не нужен: там работает
      // живая машина со своим SSE.
      if (view !== 'room') {
        roomPoll = setInterval(askRoom, ROOM_POLL_MS);
        onVisible = () => {
          // Возврат на вкладку — повод спросить сразу, а не ждать интервала.
          if (document.visibilityState === 'visible') askRoom();
        };
        document.addEventListener('visibilitychange', onVisible);
      }
    })();

    return () => {
      cancelled = true;
      if (roomPoll) clearInterval(roomPoll);
      if (onVisible) document.removeEventListener('visibilitychange', onVisible);
      controllerRef.current = null;
      transportRef.current = null;
      // Держателем быть перестали здесь: лок ещё снимается ниже, но отвечать
      // на чужую просьбу уступить эта вкладка уже не вправе.
      heldRef.current = false;
      transport?.dispose?.();
      const c = controller;
      const l = leader;
      const lp = leaderP;
      // Лок отпускаем только когда контроллер РЕАЛЬНО остановился, и кладём
      // это ожидание в handoff — следующий заход эффекта его дождётся.
      if (c) {
        setLocalHandoff(Promise.resolve(c.stop?.())
          .then(() => l?.release?.()).catch(() => undefined));
        return;
      }
      // Контроллера нет, но запрос лидерства мог ещё ВИСЕТЬ: менеджер выдаст
      // лок уже «мёртвому» заходу, и следующий, спросив ifAvailable между
      // выдачей и отпусканием, получил бы отказ навсегда (msgChat, ревью р.4).
      if (lp) setLocalHandoff(lp.then((got) => got.release?.()).catch(() => undefined));
      else l?.release?.();
    };
  }, [session, retryTick, takeTick, yielded, view, snapshotting, moved, repairing]);

  // Автоповтор ожидания собеседника. Заводится ТОЛЬКО на peer_no_device:
  // остальные отказы подъёма повтором не лечатся, и крутить их вечно значило
  // бы жечь батарею телефона и прятать настоящую поломку за «подключением».
  // blocked сбрасываем: он мог остаться от захода, которому лок не достался,
  // и без сброса экран «чат открыт в другой вкладке» пережил бы удачный повтор.
  const retryNow = () => {
    setChat(null); setBlocked(false); setTakeFailed(false);
    setRetryTick((n) => n + 1);
  };

  /**
   * «Открыть чат здесь»: просим держателя уступить и встаём за локом в очередь.
   * 🔴 Повторный тап во время переноса игнорируем (ревью Codex р.1, High-2).
   */
  const takeover = () => {
    if (taking) return;
    const next = takeTick + 1;
    askYield();
    setChat(null); setBlocked(false); setYielded(false); setTakeFailed(false);
    takingTickRef.current = next;
    setTakingTick(next);
    setTakeTick(next);
  };

  useEffect(() => {
    if (chat?.error?.kind !== 'peer_no_device') return undefined;
    const t = setTimeout(retryNow, PEER_WAIT_RETRY_MS);
    return () => clearTimeout(t);
    // 🔴 Зависимость — именно kind, а не chat: она половина защиты.
    // Расширить её до [chat] значило бы перезаводить таймер на каждый
    // снимок состояния, и тогда ожидание пошло бы на ЛЮБОМ отказе
    // подъёма (пин «автоповтор только на ожидании визави» это ловит).
  }, [chat?.error?.kind]);

  // 🔴 Хранилище участника ОДНО на браузер: если в соседней вкладке вошли
  // другим участником (или вышли), эта вкладка обязана погаснуть немедленно
  // (ревью Codex р.4). Иначе она продолжает держать лидерство — новый
  // участник упрётся в «чат открыт в другой вкладке», — а её первое же
  // продление положит СТАРУЮ сессию поверх новой, и человек, вошедший по
  // одноразовой ссылке, потеряет доступ навсегда.
  useEffect(() => {
    if (!session) return undefined;
    const onStorage = (e) => {
      // key === null — это localStorage.clear() (чистит и нашу сессию)
      if (e.key !== null && e.key !== MEMBER_SESSION_KEY) return;
      // 🔴 Читаем ЗНАЧЕНИЕ события, а не loadMemberSession(): та подмешает
      // запасную сессию из памяти этой вкладки, и выход соседней вкладки
      // выглядел бы как «ничего не изменилось» (ревью Codex р.5).
      const next = parseMemberSession(e.newValue);
      if (next && next.memberId === session.memberId) return;
      leavingRef.current = true;
      transportRef.current?.dispose?.();
      forgetMemberSession(); // хранилище чужое — трогаем только свою память
      setReplaced(true);
      setSession(null);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [session]);

  useEffect(() => {
    listRef.current?.scrollTo(0, listRef.current.scrollHeight);
  }, [chat?.messages]);

  // Запрет отправки (решение 07.09): ключ на той стороне сменился, человек
  // ещё не решил. Правило считает КОНТРОЛЛЕР (форма одна у 1:1 и комнаты),
  // страница только читает.
  const sendStopped = Boolean(chat?.sendBlocked);

  const send = () => {
    const text = input.trim();
    // ATT С3: файл шлётся из ОБОИХ видов — и в личный чат, и в комнату
    // (RoomController вложения умеет с С3, контракт send у обоих одинаковый).
    const draft = file;
    // 🔴 При запрете НЕ очищаем поле: молчаливая очистка съела бы текст.
    // 🔴 И `uploading` — здесь же, а не только погашенной кнопкой: Enter шёл
    // мимо неё, а загрузка вложения идёт ВНЕ очереди контроллера, поэтому
    // текст обгонял сообщение с файлом (ревью 12.09, р.5, Medium).
    if (sendStopped || Boolean(chat?.uploading)
        || (!text && !draft) || !controllerRef.current) return;
    const quote = replyTo;
    setInput('');
    if (draft) setFile(null);
    setReplyTo(null);
    // 🔴 Отказ контроллера (`false`) — запрет выставился, пока клик летел,
    // или вложение не загрузилось: черновик (и текст, и файл) возвращается,
    // если человек не начал собирать новое (ревью WEB-14 р.1, Medium; в
    // комнате случай мягче — её аутбокс хранит текст ДО доставки).
    // Канал, из которого шлём: исход применим ТОЛЬКО к нему.
    const sentFrom = view;
    Promise.resolve(controllerRef.current.send(text, draft ? [draft] : [], quote))
      .then((sent) => {
        // 🔴 Пока отправка летела, человек мог уйти на другую вкладку.
        // Вернуть текст в общее поле значит подставить личную реплику под
        // кнопку «Отправить» ОБЩЕЙ комнаты — и наоборот. Лучше потерять
        // черновик в редком отказе, чем отправить его не тем людям.
        if (sent === false && viewRef.current === sentFrom) {
          setInput((cur) => cur || text);
          if (draft) setFile((cur) => cur || draft);
          // 🔴 Цитата возвращается по ТОМУ ЖЕ правилу, что текст и файл:
          // только в свой канал. Иначе реплика из личной переписки встала бы
          // под кнопку «Отправить» ОБЩЕЙ комнаты.
          if (quote) setReplyTo((cur) => cur || quote);
        }
      })
      .catch(() => {});
  };

  // WEB-22 (Д-19): выход теперь двухшаговый. Первый шаг только спрашивает —
  // ничего не глушит и ничего не стирает, поэтому промах пальцем обратим.
  const askLeave = () => {
    setFootOpen(true);
    setLeaveAsk(true);
  };

  const leave = () => {
    // 🔴 Порядок обязателен: сначала глушим транспорт, потом чистим хранилище.
    // Наоборот — летящее продление успевало положить свежий токен обратно, и
    // «выход» отменялся сам собой (ревью Codex р.3). Cleanup эффекта придёт
    // только на следующем коммите — на это окно гонки его не хватает.
    leavingRef.current = true;
    transportRef.current?.dispose?.();
    clearMemberSession();
    setSession(null);
  };

  const body = () => {
    if (replaced) {
      return (
        <p className="msgChatNote" data-testid="member-replaced">
          На этом устройстве открыт чат другого участника — вход по новой ссылке
          был сделан в другой вкладке. Эта вкладка больше не подключена; работайте
          в той, где вы вошли последней.
        </p>
      );
    }
    if (!session) {
      /**
       * 🔴 WEB-28. Здесь стоял тупик: «откройте ссылку-приглашение и нажмите
       * „Открыть чат в браузере“». Он был не просто бесполезен, а ВРЕДЕН —
       * повторный вход по ссылке плодит ДУБЛЬ участника: в списке появляется
       * второй человек, а переписка остаётся у первого. Плюс он ссылался на
       * кнопку с экрана, которого у человека перед глазами нет.
       *
       * Теперь два честных пути и ни одного совета, который делает хуже.
       */
      return (
        <div className="msgChatNote" data-testid="member-no-session">
          <p>Чат ещё не открыт на этом устройстве.</p>
          <p>
            <strong>Меня пригласили впервые.</strong>
            {' '}
            Откройте ссылку-приглашение, которую вам прислали, — она приведёт
            сюда сама.
          </p>
          <p>
            <strong>У меня уже есть чат на другом устройстве.</strong>
            {' '}
            🔴 Не открывайте приглашение второй раз — так появится второй
            участник, а переписка останется на прежнем устройстве. Возьмите её
            с собой: на том устройстве откройте чат → «Ещё» → «Копия и перенос»
            → «Перенести чат на другое устройство», а файл принесите сюда.
          </p>
          <p>
            <a href={LANDING_PATH} data-testid="member-no-session-transfer">
              Перенести чат с другого устройства
            </a>
          </p>
        </div>
      );
    }
    if (lost) {
      return (
        <p className="msgChatNote msgChatError">
          Доступ к чату закрыт: приглашение отозвали или участника удалили.
          Попросите новый QR-код.
        </p>
      );
    }
    /**
     * Д-3 «хвост»: лечение из копии. Ветка стоит ВЫШЕ всех «чат не поднялся»
     * намеренно — чат к этому моменту уже остановлен нами, и его последний
     * снимок (тот самый отказ 409) не должен перебивать панель.
     */
    if (repairing) {
      // Занятая база лечится повтором (закрыть соседние вкладки), отсутствие
      // сессии — нет: разные беды, разные тексты, разные кнопки.
      // 🔴 Текст не называет причину, которой код НЕ ЗНАЕТ (ревью р.2, Low):
      // `deleteDatabase` сводит «база занята» и «браузер отказал» в один
      // отказ. Поэтому здесь сказано, что известно точно (стереть не вышло),
      // предложен повтор — и назван выход на случай, когда повтор не помогает
      // никогда: в приватном окне он бесполезен.
      // 🔴 Запрещённое хранилище — СВОЙ текст (ревью р.4). Здесь причина
      // известна точно, и общий совет был бы вредным: «перезагрузите
      // страницу» прямо противоречит плашке этой же страницы («переписка
      // пропадёт, если закрыть или перезагрузить вкладку»), а «попробуйте
      // снова» не сработает никогда — модуль падает ещё до первого удаления.
      if (repairFailed === 'no_storage') {
        return (
          <p className="msgChatNote msgChatError" data-testid="member-repair-no-storage">
            {'Браузер не разрешает этому сайту хранить данные, поэтому'
              + ' восстановить переписку здесь нельзя. Откройте чат в обычном'
              + ' окне (не в приватном) — понадобится новая ссылка-приглашение:'
              + ' вход, сделанный здесь, туда не переедет.'}
          </p>
        );
      }
      if (repairFailed === 'blocked' || repairFailed === 'failed') {
        return (
          <p className="msgChatNote msgChatError" data-testid="member-repair-blocked">
            {'Не удалось стереть повреждённые данные на этом устройстве.'
              + ' Попробуйте снова, а если не помогает — перезагрузите страницу'
              + ' или закройте и откройте вкладку заново. Когда и это не'
              + ' помогает, вернуть переписку на этом устройстве не выйдет:'
              + ' попросите новую ссылку-приглашение. '}
            <button type="button" data-testid="member-repair-again" onClick={startRepair}>
              Попробовать снова
            </button>
          </p>
        );
      }
      if (repairFailed) {
        return (
          <p className="msgChatNote msgChatError" data-testid="member-repair-failed">
            Восстанавливать нечего: на этом устройстве не осталось данных о вашей
            переписке. Войдите заново по новой ссылке-приглашению.
          </p>
        );
      }
      // 🔴 Панель показываем ТОЛЬКО после того, как чат остановлен и мёртвый
      // стор снесён. Иначе человек успел бы нажать «Восстановить» раньше
      // удаления — и запись из копии гонялась бы с `deleteDatabase` за одни и
      // те же базы. Ожидание короткое, но полагаться тут на скорость пальца
      // нельзя: это ровно тот класс гонок, что стоил нам прогона E3.
      if (!repairOpen) {
        return (
          <p className="msgChatNote" data-testid="member-repair-wait">
            Останавливаем чат на этом устройстве…
          </p>
        );
      }
      return (
        <div className="msgChatNote" data-testid="member-repair">
          <p>
            Выберите файл копии (.totalchat) и введите код, который вы записали,
            когда её снимали.
          </p>
          <DeviceImportPanel
            mode="repair"
            /* 🔴 Чей файл мы согласны принять (ревью 03.09, High). Иначе любая
               валидная копия — от прежнего участия этого же человека или от
               соседа по общему телефону — перезаписала бы сессию, did и пароль
               стора, а вернуться было бы уже некуда: свой стор снесён. */
            expect={{ memberId: session.memberId, did: session.did }}
            /* 🔴 Проверяем ЖИВЫМ токеном, а не тем, что лежит в файле (ревью,
               Low). Копия старше года дала бы 401 и экран «доступ закрыт» —
               враньё: доступ жив, просрочен только файл. Живой токен читаем из
               хранилища: в состоянии он устаревает по построению, продление
               живёт в транспорте и в localStorage. */
            onCheck={(fromFile) => refreshMemberToken(
              loadMemberSession()?.token ?? fromFile,
            )}
            /* 🔴 Только перезагрузкой. Ключи из копии читает МАШИНА при
               подъёме, а эта страница уже живёт со своим остановленным
               контроллером и старым снимком; поднимать чат «на месте» значило
               бы повторить сборку состояния, которую делает загрузка. */
            onDone={reload}
            /* Отказ на полпути возвращает туда же, откуда пришли: стор
               мёртв, и честнее показать тот же экран заново, чем оставить
               человека на панели, которая больше ничего не чинит. */
            onClose={reload}
          />
        </div>
      );
    }
    if (taking) {
      return <p className="msgChatNote" data-testid="chat-taking">Переносим чат сюда…</p>;
    }
    if (yielded) {
      return (
        <p className="msgChatNote" data-testid="chat-yielded">
          Чат перенесён в другую вкладку — работайте там.
          {' '}
          <button type="button" onClick={takeover} disabled={taking}>Вернуть сюда</button>
        </p>
      );
    }
    if (blocked) {
      return (
        <p className="msgChatNote" data-testid="chat-blocked">
          {takeFailed
            ? 'Не удалось перенести чат: та вкладка не ответила. Попробуйте ещё раз или закройте её.'
            : 'Чат открыт в другой вкладке этого браузера.'}
          {canAskYield() && (
            <>
              {' '}
              <button type="button" onClick={takeover} disabled={taking}>
                Открыть чат здесь
              </button>
            </>
          )}
          {!canAskYield() && ' Работайте там или закройте её и обновите эту.'}
        </p>
      );
    }
    if (!chat || chat.phase === 'init') {
      return <p className="msgChatNote">Подключение к защищённому чату…</p>;
    }
    // Ожидание собеседника — не ошибка: он просто ещё не открыл чат. Экран
    // ждёт сам, кнопка — для нетерпеливых (и чтобы ожидание не выглядело
    // зависшим); красным это НЕ красим.
    /**
     * Д-3 «хвост»: крипто-стор погиб, did цел. До этого среза здесь стояло
     * «Крипта не поднялась: HTTP 409 msg/devices …» — правда, из которой не
     * следует ни одного действия, да ещё и с кнопкой «Проверить сейчас»,
     * которая не могла помочь никогда: 409 детерминирован.
     *
     * Лечение ровно одно и настоящее — положить обратно прежние ключи из
     * копии. Второй выход честно назван вторым: он работает всегда, но
     * стоит прежней переписки и лишней строки в списке у владельца.
     */
    if (chat.phase === 'error' && chat.error?.kind === 'device_identity_lost') {
      return (
        <div className="msgChatNote msgChatError" data-testid="member-identity-lost">
          <p>{chat.error.text}</p>
          <p className="memberRepairActions">
            <button
              type="button"
              data-testid="member-repair-start"
              onClick={startRepair}
            >
              Восстановить из копии
            </button>
            {' '}
            <button
              type="button"
              data-testid="member-repair-leave"
              onClick={askLeave}
            >
              Выйти и войти заново
            </button>
          </p>
          <p className="memberRepairHint">
            Копия — это файл .totalchat, который вы сохраняли кнопкой «Копия и
            перенос», и код к нему. Без копии остаётся только войти заново: для
            этого понадобится новая ссылка от владельца, а прежние сообщения не
            вернутся.
          </p>
        </div>
      );
    }
    if (chat.phase === 'error' && chat.error?.kind === 'peer_no_device') {
      return (
        <p className="msgChatNote" data-testid="member-peer-waiting">
          {chat.error.text}
          {' '}
          <button type="button" onClick={retryNow}>Проверить сейчас</button>
        </p>
      );
    }
    if (chat.phase === 'error' && !chat.error?.retriable) {
      return <p className="msgChatNote msgChatError">{chat.error?.text ?? 'Чат не поднялся.'}</p>;
    }
    return (
      <>
        {/* 🔴 Закреплённые предупреждения живут ровно там же, где полные
            плашки, которые они пересказывают, — в ГОТОВОМ состоянии.
            Ненадолго вынесенные наружу `body()`, они переживали выход из чата
            и замену сессии соседней вкладкой: экран уже говорил «вы не вошли»,
            а сверху висело «ключ СМЕНИЛСЯ» с кнопкой в никуда (ревью Codex
            30.08, Medium). Правило простое: сводка не имеет права жить дольше
            того, что она сводит. */}
        {/* 🔴 WEB-23: `firstUse` сюда больше не передаётся — «первый контакт»
            переехал в панель «Защита переписки» (он горел у всех и всегда:
            снимается только кнопкой «Код сверен»). `fragile` ОСТАЁТСЯ: это не
            рассказ об устройстве шифрования, а предупреждение, что закрытая
            вкладка унесёт с собой всю переписку. */}
        <PinnedWarnings
          changed={chat.trust?.status === 'key_changed' ? 1 : 0}
          fragile={fragile}
          onShow={() => listRef.current?.scrollTo(0, 0)}
        />
        {/* 🔴 Прокручивается ВСЯ надстройка вместе с лентой (см. msgChat.css,
            `.msgScroll`). Плашки доверия и код безопасности, оставаясь
            неподвижными, съедали экран телефона: в комнате живой замер 30.08
            дал шесть плашек по 113px при области 648. Прижат только ввод. */}
        <div
          className="msgScroll"
          ref={listRef}
          // 🔴 Прокручиваемая область обязана быть достижима с клавиатуры
          // (ревью Codex 30.08, Medium): без `tabIndex` до истории не
          // добраться ни стрелками, ни PageUp — Tab уходит сразу в ввод.
          tabIndex={0}
          role="region"
          aria-label="Переписка и предупреждения безопасности"
          data-testid="msg-scroll"
        >
        {fragile && (
          <div className="msgTrustBanner" data-testid="member-fragile">
            <span>
              Браузер не разрешил сохранить чат на этом устройстве (обычно это
              приватный режим). Переписка работает, но пропадёт, если закрыть
              или перезагрузить вкладку — а ссылка-приглашение одноразовая.
              Откройте её в обычном окне, чтобы чат сохранился.
            </span>
          </div>
        )}
        {/* WEB-23: полная плашка осталась ТОЛЬКО у смены ключа; «первый
            контакт» и код безопасности живут в панели «Защита переписки». */}
        {chat.trust?.status === 'key_changed' && (
          <div className="msgTrustBanner msgTrustDanger">
            <span>{TRUST_TEXT.key_changed}</span>
            <button type="button" onClick={() => controllerRef.current?.markVerified()}>
              Код сверен
            </button>
          </div>
        )}
        <ul className="msgList">
          {/* 🔴 Пустое состояние. У владельца оно есть с самого начала, у
              участника его не было: человек переключался в общую комнату,
              где ещё никто не писал, и видел просто пустое место над полем
              ввода — неотличимое от «не загрузилось». Текст разный, потому
              что и ситуация разная: в комнате ответ увидят все. */}
          {(chat.messages ?? []).length === 0 && (
            <li className="memberEmpty" data-testid="member-empty">
              {view === 'room'
                ? 'Здесь пока никто ничего не писал. Напишите первым — увидят все.'
                : 'Здесь пока пусто. Напишите первым.'}
            </li>
          )}
          {(chat.messages ?? []).map((m) => (
            <li
              key={m.messageId}
              data-testid={`member-msg-${m.messageId}`}
              data-message-id={m.messageId}
              // tabIndex -1: строка не встаёт в обход по Tab, но МОЖЕТ принять
              // фокус при переходе к оригиналу (WEB-16 срез 4)
              tabIndex={-1}
              className={`msgItem ${m.direction === 'out' ? 'msgOut' : 'msgIn'} ${m.pending ? 'msgPending' : ''}`
                + `${highlighted === m.messageId ? ' msgItemFound' : ''}`}
            >
              {/* В комнате пишут несколько человек — без подписи не понять,
                  кто. В личной переписке собеседник один, и подпись была бы
                  шумом. */}
              {view === 'room' && m.direction !== 'out' && (
                <span className="roomSender" data-testid="member-sender">
                  {senderName(chat, m.senderType, m.senderId)}
                </span>
              )}
              {/* WEB-16: цитата НАД текстом. Оригинал ищется только в ЭТОЙ
                  ленте: у участника их две в одном экране, и поиск «где
                  угодно» показал бы сообщение из другого канала. Комната —
                  срез 3, здесь только личная переписка. */}
              {quotesHere && (
                <ReplyQuote
                  quote={m.replyTo}
                  original={m.replyTo ? byId.get(m.replyTo.messageId) : null}
                  names={view === 'room'
                    ? { authorOf: (orig) => knownSenderName(chat, orig.senderType, orig.senderId) }
                    : { peerName }}
                  onJump={jumpTo}
                />
              )}
              <span className="msgText">
                {/* Сообщение-файл приходит с пустым body — это не «не
                    расшифровано», недоверие выражается null */}
                {m.text ?? (m.attachments?.length ? '' : 'Сообщение не удалось расшифровать')}
              </span>
              <MsgAttachments
                attachments={m.attachments}
                fetchBlob={(meta) => controllerRef.current.downloadAttachment(meta)}
              />
              <span className="msgMeta">
                {m.pending ? 'отправляется…' : m.sentAt ? new Date(m.sentAt).toLocaleString() : ''}
                {quotesHere && !m.pending && !m.decryptFailed
                  && m.direction !== 'system' && (
                  <ReplyButton
                    disabled={chat.phase !== 'ready'}
                    onClick={() => {
                      // В комнате имя автора берётся из РОСТЕРА (их много),
                      // в личной переписке — имя визави. Своё сообщение
                      // уезжает с ПУСТЫМ именем: «Вы» у получателя значило бы
                      // другого человека.
                      // 🔴 В комнате имя берётся из ростера ДЛЯ ОБОИХ
                      // направлений: авторов много, и тот, у кого оригинала нет
                      // (вошёл позже), по пустому имени автора не восстановит
                      // (находка ревью, Medium). В личной переписке своё имя
                      // уезжает ПУСТЫМ: там автором окажется визави получателя.
                      const snap = view === 'room'
                        ? replySnapshot(
                          m, knownSenderName(chat, m.senderType, m.senderId) ?? '',
                        )
                        : replySnapshot(m, m.direction === 'out' ? '' : peerName);
                      // `mine` живёт только на этом экране — см. ReplyDraftBar
                      setReplyTo(snap && { ...snap, mine: m.direction === 'out' });
                    }}
                  />
                )}
              </span>
            </li>
          ))}
        </ul>
        </div>
        {chat.error && (
          <div className="msgChatError msgSendError">
            <span>{chat.error.text}</span>
            {chat.error.retriable && (
              <button type="button" onClick={() => controllerRef.current?.retryPending()}>
                Повторить
              </button>
            )}
          </div>
        )}
        <SendBlocked
          blocked={chat.sendBlocked}
          onSendAnyway={() => controllerRef.current?.sendAnyway()}
        />
        <AttachmentDraft file={file} onClear={() => setFile(null)} />
        <ReplyDraftBar quote={replyTo} onCancel={() => setReplyTo(null)} />
        <UploadingNote uploading={chat.uploading} />
        <div className="msgInputRow">
          {/* C2a: бокс — в ОБОИХ видах; со среза С3 и скрепка в обоих —
              комната умеет вложения. Черновик-файл ЖИВЁТ через переключение
              вкладок (тот же file-стейт), но уходит только из того вида, где
              нажали «Отправить». */}
          <div className="msgInputBox" data-testid="input-box">
            <input
              type="text"
              ref={inputRef}
              value={input}
              placeholder="Сообщение…"
              disabled={chat.phase !== 'ready'}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            />
            {/* Скрытый input — скрепка живёт кнопкой: нативный контрол не
                стилизуется и ломал бы строку ввода */}
            <input
              type="file"
              ref={fileRef}
              hidden
              data-testid="attach-input"
              onChange={(e) => {
                const picked = e.target.files?.[0];
                if (picked) setFile(picked);
                // тот же файл можно выбрать повторно после ✕
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="msgAttachBtn"
              title="Прикрепить файл"
              aria-label="Прикрепить файл"
              disabled={chat.phase !== 'ready' || Boolean(chat.uploading)}
              onClick={() => fileRef.current?.click()}
            >
              📎
            </button>
            {/* WEB-31: отправка — ВНУТРИ бокса, справа от скрепки. Появляется,
                когда есть что отправлять; при запрете отправки и во время
                загрузки видна и погашена (правило — в самом компоненте). */}
            <SendButton
              inputRef={inputRef}
              text={input}
              file={file}
              ready={chat.phase === 'ready'}
              sendStopped={sendStopped}
              uploading={chat.uploading}
              onSend={send}
            />
          </div>
        </div>
      </>
    );
  };

  const peerName = chat?.peer?.name ?? session?.peer?.name;

  // Своя подпись видна только из ростера комнаты: сессия участника её не
  // хранит, а в личном чате ростера нет. Поэтому и правка живёт на вкладке
  // комнаты — там же, где подпись показывается другим.
  // WEB-19: своя подпись нужна ВЕЗДЕ, а не только в комнате. Пока она бралась
  // из ростера комнаты, у человека на вкладке личного чата (и у любого, кого в
  // комнату не звали) не было НИКАКОГО способа поменять имя — и заметной
  // дверью оставалась ссылка-приглашение, которая заводит нового участника
  // (разбор дублей 09.09, тикет WEB-19).
  // Ростер комнаты остаётся ПЕРВЫМ источником: он свежее сессии, если имя
  // меняли с другого устройства. Сессия — запасной, и её может не быть у
  // тех, кто вошёл до этого среза; тогда ведём себя как раньше.
  const selfNameFromRoom = view === 'room' && chat?.self?.key
    ? chat?.membersByPrincipal?.[chat.self.key]?.displayName ?? null
    : null;
  const selfName = selfNameFromRoom ?? selfNameOwn;
  // Вкладка, в которой правка подписи действительно может сработать: сессия
  // есть, чат поднят (`chat` приходит от контроллера) и вкладку не погасили.
  // Тот же набор признаков, что у вкладки комнаты ниже.
  // 🔴 Чего здесь СОЗНАТЕЛЬНО нет: подтягивания имени из ростера в свою копию.
  // Ревью предлагало это (р.6, Low: имя, изменённое с ДРУГОГО устройства, в
  // личном чате остаётся старым), и правка была написана — а следующий раунд
  // показал, что она заводит гонку с собственным переименованием: сразу после
  // PATCH ростер ещё старый, и синхронизация вернула бы старое имя поверх
  // нового. Лекарство хуже болезни: в комнате свежее имя видно и так (ростер
  // идёт первым источником), а редкий перекос в личном чате чинится
  // перезаходом. Оставлено открытым в тикете WEB-19.
  // 🔴 `repairing` тут наравне с `moved` (ревью 03.09, Medium): состояние
  // такое же терминальное — выход только перезагрузкой. Без него на экране
  // лечения оставались бы живыми «Копия и перенос» (снимок ушёл бы в ТОЛЬКО ЧТО
  // снесённый стор и дал бы файл без ключей), правка подписи (через уже
  // обнулённый транспорт) и «Защита переписки», а на экране отказа, наоборот,
  // пряталась бы кнопка выхода — та самая, которую текст и советует нажать.
  // 🔴 Погибший стор гасит чат ЭКРАНОМ РАНЬШЕ, чем начнётся лечение (ревью
  // р.2, Medium). Иначе испуганный человек первым делом жмёт «сохранить
  // копию» — и получает файл с настоящим на вид кодом, внутри которого лежит
  // тот самый отвергнутый сервером аккаунт: копия, восстанавливающая ровно
  // тот же 409. Хуже того, ею можно затереть настоящую старую копию.
  const identityLost = chat?.phase === 'error'
    && chat?.error?.kind === 'device_identity_lost';
  // 🔴 `!repairing` тут сегодня ИЗБЫТОЧЕН и мутантом не убивается (ревью р.4):
  // лечение заводится только с экрана «данные повреждены», снимок чата при
  // этом не сбрасывается, поэтому `identityLost` держится всё лечение и
  // прячет то же самое. Терм оставлен осознанно — чтобы гашение не зависело
  // от этого неписаного порядка, — но за проверенную защиту не выдаётся.
  const chatAlive = Boolean(session) && Boolean(chat)
    && !yielded && !blocked && !lost && !replaced && !moved && !repairing
    && !identityLost;

  /**
   * Д-3: снимок устройства для файла.
   *
   * 🔴 `flushSync`, а не пауза на глазок. Транспорт обязан замолчать ДО
   * первого чтения: иначе между снимком истории и снимком крипто-стора
   * приедет сообщение, стор уедет вперёд истории — и на новом устройстве это
   * сообщение не расшифруется уже никогда (рачет ушёл, а в файле его нет).
   * `flushSync` заставляет React прогнать уборку эффекта здесь и сейчас, и
   * только после неё начинается чтение.
   */
  const runExport = async (kind) => {
    flushSync(() => setSnapshotting(true));
    // 🔴 Ждём, пока контроллер РЕАЛЬНО остановился (ревью р.2, Low). Транспорт
    // после уборки эффекта уже молчит, но `stop()` резолвится лишь после
    // хвоста очереди: начатый `_catchUp` успел бы записать продвинутую
    // Olm-сессию в стор ПОСЛЕ снимка истории — то самое «стор уехал вперёд
    // истории», которое запрещает комментарий над этой же функцией.
    await waitLocalHandoff();
    try {
      return await exportDevice({ kind, roomIds: roomId ? [roomId] : [] });
    } finally {
      // Транспорт поднимается обратно сам: эффект перезапустится по зависимости.
      setSnapshotting(false);
    }
  };

  /**
   * Д-3, шаг 2 переноса: погасить это устройство (дизайн §2 — иначе две живые
   * копии разойдутся по рачету, и собеседник перестанет расшифровывать обе).
   *
   * 🔴 Порядок: СНАЧАЛА погасить чат, ПОТОМ стирать. Живая машина держит
   * открытое соединение с крипто-стором, и `deleteDatabase` при нём упирается
   * в `blocked` — молча, без ошибки. Живой прогон E3 поймал именно это: база
   * `…::matrix-sdk-crypto` с identity-ключами оставалась на диске, а экран уже
   * говорил человеку, что переписки здесь больше нет. `flushSync` заставляет
   * React прогнать уборку эффекта здесь и сейчас, `waitLocalHandoff` ждёт,
   * пока контроллер РЕАЛЬНО остановился и закрыл стор.
   */
  const runWipe = async () => {
    leavingRef.current = true;
    flushSync(() => setMoved(true));
    await waitLocalHandoff();
    transportRef.current?.dispose?.();
    await wipeDevice({ roomIds: roomId ? [roomId] : [] });
  };

  /**
   * Д-3 «хвост»: «Восстановить из копии» на устройстве, где погиб крипто-стор.
   *
   * 🔴 Тот же порядок, что и в `runWipe`, и по той же причине: сперва
   * ОСТАНОВИТЬ чат (flushSync прогоняет уборку эффекта здесь и сейчас,
   * waitLocalHandoff ждёт, пока контроллер реально закрыл стор), и только
   * потом трогать базы. Живая машина держит соединение, и удаление молча
   * упирается в `blocked` — это уже стоило нам одного живого прогона (E3).
   *
   * 🔴 Мёртвый стор сносим ДО панели, а не после успешного импорта. Он ничего
   * не стоит: его ключи сервер отверг (409), а с ними на диске машина при
   * следующем подъёме снова заведёт тот же тупик. Переписка и ключи
   * localStorage при этом целы — `dropCryptoStore` их не трогает.
   */
  const startRepair = async () => {
    // Занятость держит ref, а не состояние: «Попробовать снова» на экране
    // отказа зовёт этот же ход, когда `repairing` уже true.
    // 🔴 Честно про этот пояс: сегодня он НЕДОСТИЖИМ, и мутант «убрать его»
    // выживает (проверено). Второй тап не доходит до обработчика, потому что
    // `flushSync` ниже успевает убрать кнопку из DOM, а отсоединённый узел до
    // делегированного слушателя React не достаёт. Пояс оставлен на случай,
    // когда экран перестанет исчезать синхронно, — и оставлен ОСОЗНАННО, а не
    // как «защита, которую мы проверили».
    if (repairBusyRef.current) return;
    repairBusyRef.current = true;
    leavingRef.current = true;
    setRepairFailed(null);
    flushSync(() => setRepairing(true));
    // Уборка эффекта, которую только что прогнал flushSync, сама остановила
    // контроллер и погасила транспорт; ждём, пока остановка ДОШЛА до конца —
    // `stop()` резолвится, лишь когда стор закрыт.
    await waitLocalHandoff();
    try {
      const { dropped } = await dropCryptoStore();
      // 🔴 Незакрытая база — не мелочь: файл лёг бы поверх живого стора, и
      // старые сессии на диске пережили бы «восстановление» (тупик E2).
      if (dropped) setRepairOpen(true);
      else setRepairFailed('blocked');
    } catch (e) {
      // Сессии нет — восстанавливать некуда, нужна новая ссылка. Всё
      // остальное (браузер не дал хранилище, приватное окно) лечится иначе,
      // и валить это в один текст значит отправить человека делать не то.
      // 🔴 `fragile` здесь обязателен (ревью р.3). Когда браузер запретил
      // хранилище, сессия живёт только в памяти вкладки, а `readDeviceIdentity`
      // читает localStorage — и честно не находит ничего. Без этой поправки
      // человек с работающим чатом на экране услышал бы «на этом устройстве не
      // осталось данных о вашей переписке» и пошёл бы за новой ссылкой,
      // которая ляжет ровно так же.
      if (fragile) setRepairFailed('no_storage');
      else setRepairFailed(e?.code === 'no_session' ? 'no_session' : 'failed');
    }
    repairBusyRef.current = false;
  };

  const closeSelfRename = () => {
    returnSelfFocus.current = true;
    setRenamingSelf(false);
  };

  const openSelfRename = () => {
    setSelfDraft(selfName ?? '');
    setSelfError(null);
    setRenamingSelf(true);
  };

  // Фокус: при открытии — в поле, при закрытии — обратно на кнопку (правило
  // формы переименования комнаты: иначе фокус уезжает на body).
  useEffect(() => {
    if (renamingSelf) { selfInputRef.current?.focus(); return; }
    if (returnSelfFocus.current) {
      returnSelfFocus.current = false;
      selfOpenRef.current?.focus();
    }
  }, [renamingSelf]);

  /**
   * WEB-22 (Д-19), ревью: тот же приём для вопроса о выходе — и он тут не про
   * вежливость, а про работоспособность.
   *
   * 🔴 Блок дописывается в НИЗ карточки, а прокрутка живёт на ней самой
   * (`.memberChatCard { overflow-y: auto }`), не на документе. На целевом
   * экране — iPhone SE в мини-браузере, 375×470 — обе кнопки оказываются на
   * y≈542..586, то есть за сгибом: человек видит красную кромку и решает,
   * что кнопка сломалась (замер ревью: скрыто 139 px, с клавиатурой 279).
   * `focus()` прокручивает контейнер к элементу сам, поэтому одна строка
   * чинит и доступность, и видимость. При закрытии фокус возвращается на
   * «Выйти», иначе он улетает на body — уже проверенная тут беда.
   */
  useEffect(() => {
    if (leaveAsk) { leaveCancelRef.current?.focus(); return; }
    if (returnLeaveFocus.current) {
      returnLeaveFocus.current = false;
      leaveOpenRef.current?.focus();
    }
  }, [leaveAsk]);

  // 🔴 Переключение вкладки закрывает форму (ревью р.1): иначе она переживала
  // бы уход из комнаты, а submit из личного чата дёргал бы refresh уже у
  // ДРУГОГО контроллера (ChatController) — обещанное «ростер перечитан сразу»
  // молча терялось бы.
  useEffect(() => {
    setRenamingSelf(false);
    setSelfError(null);
    returnSelfFocus.current = false;
    // 🔴 И вопрос о выходе тоже (ревью WEB-22): человек открыл его в личном
    // чате, передумал и ушёл в комнату — под комнатой висело бы «станет
    // недоступна навсегда». Контекст сменился, разговор об опасном действии
    // обязан закрыться вместе с ним. `footOpen` не трогаем: он про раскладку.
    setLeaveAsk(false);
    returnLeaveFocus.current = false;
  }, [view]);

  // WEB-19 §4.3 (Д-2): пришёл с лендинга менять подпись — форма открывается
  // сама, как только чат жив (раньше PATCH ушёл бы в мёртвый транспорт).
  useEffect(() => {
    if (!chatAlive || !arrival?.rename) return;
    setArrival(null);
    setSelfDraft(selfName ?? '');
    setSelfError(null);
    setRenamingSelf(true);
  }, [chatAlive, arrival, selfName]);

  const submitSelfRename = async (e) => {
    e.preventDefault();
    // 🔴 Пояс от двойного submit (ревью р.2): Enter в поле шлёт форму мимо
    // погашенной кнопки, и второй PATCH ушёл бы, пока первый в полёте.
    if (savingSelf) return;
    const name = selfDraft.trim();
    if (!name) {
      setSelfError('Подпись не может быть пустой.');
      return;
    }
    if (name === selfName) { closeSelfRename(); return; }
    setSavingSelf(true);
    setSelfError(null);
    try {
      // Тем же member-транспортом, что и весь чат: второй транспорт завёл бы
      // второй цикл продления одного токена (урок WEB-12).
      const api = new MsgApi(session.did, transportRef.current);
      await api.renameProfile(name);
      // WEB-19: новая подпись — и в сессию. Иначе на вкладке личного чата (где
      // ростера комнаты нет) подвал показывал бы СТАРОЕ имя до перезахода, и
      // человек решил бы, что смена не сработала — ровно та развилка, на
      // которой он идёт менять имя через ссылку-приглашение.
      // 🔴 Пишем ОДНО ПОЛЕ, а не сессию целиком: у токена три копии, и любая
      // сборка «сессия + новое имя» заставляет выбирать, чей токен писать.
      // renameStoredSession такого выбора не делает — подпись ложится поверх
      // той сессии, что уже в слоте (ревью Codex р.1-р.4, одна линия находок).
      // 🔴 Исход обязателен: слот мог достаться другому участнику, пока PATCH
      // летел. Тогда вкладку гасим, а форму не закрываем и ростер не трогаем —
      // чата здесь больше нет.
      if (!applyWriteOutcome(renameStoredSession(name, session.memberId))) return;
      setSelfNameOwn(name);
      closeSelfRename();
      // Ростер перечитывается сразу — человек видит новую подпись, не гадая
      controllerRef.current?.refresh?.();
    } catch (err) {
      setSelfError(err?.status === 422
        ? 'Подпись — от 1 до 64 символов, одной строкой.'
        : 'Не удалось изменить подпись — попробуйте ещё раз.');
    } finally {
      setSavingSelf(false);
    }
  };

  // WEB-23: своё устройство для панели — тем же member-транспортом, что и весь
  // чат (второй транспорт завёл бы второй цикл продления токена, урок WEB-12).
  // Спрашиваем только когда панель открыли; отказ её не ломает.
  // 🔴 `chat?.phase` в зависимостях — не украшение: `transportRef` это REF, он
  // не реактивен. Замок отрисован уже во время «Подключение к защищённому
  // чату…», и клик в эти миллисекунды давал заход с пустым транспортом без
  // единого шанса на повтор — раздел «Это устройство» молча пропадал до
  // закрытия и повторного открытия панели (ревью 03.09, Low).
  useEffect(() => {
    if (!securityOpen || myDevice || !session?.did || !transportRef.current) return undefined;
    let alive = true;
    new MsgApi(session.did, transportRef.current).myDevice()
      .then((info) => { if (alive) setMyDevice(info ?? null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [securityOpen, myDevice, session, chat?.phase]);

  // 🔴 В КОМНАТЕ у участника доверие лежит в `trustByPrincipal` (там работает
  // RoomController), а не в `chat.trust` — ревью 03.09, Medium. Без этой ветки
  // панель обещала бы «кто в разговоре» и показывала пустоту, а цифра у замка
  // стояла бы на нуле при любом числе непроверенных.
  // 🔴 И только при ЖИВОМ чате: после уступки вкладки, замены сессии и выхода
  // снимок не обнуляется, а кнопка «Код сверен» звала бы контроллер, которого
  // уже нет. Правило записано ещё 30.08: сводка не имеет права жить дольше
  // того, что она сводит.
  const securityPeople = (() => {
    if (!chatAlive) return [];

    if (view === 'room') {
      return Object.entries(chat?.trustByPrincipal ?? {}).map(([key, t]) => {
        const [type, id] = key.split(':');
        return { key, name: senderName(chat, type, Number(id)), status: t?.status };
      });
    }
    return chat?.trust
      ? [{ key: 'peer', name: peerName ?? 'Собеседник', status: chat.trust.status }]
      : [];
  })();

  return (
    <main className="MemberChat">
      <div className="memberChatCard">
        <h2 className="msgChatTitle">
          {/* 🔴 Название обязано быть отдельным flex-элементом. Голым текстом
              внутри flex-контейнера оно не сжимается, и длинное имя комнаты
              (поле переименования допускает 64 символа) выталкивало значок
              «🔒 E2E» за край экрана: замер 30.08 на 360px — правый край
              значка 753. Пропадала единственная пометка, что переписка
              шифруется. */}
          <span className="memberChatTitleText">
            {view === 'room'
              ? (chat?.room?.title ?? 'Общий чат')
              // WEB-30: просто имя, как в мессенджерах. «Чат с ${имя}» ставил
              // подпись владельца в именительный падеж после «с» — «Чат с
              // Александр, Алексей».
              // `||`, а не `??`: пустая подпись владельца — тоже не заголовок
              // (ревью WEB-30, P3).
              : (peerName || 'Защищённый чат')}
          </span>
          {/* WEB-23: замок остался (признак защиты обязан быть виден всегда),
              но перестал быть надписью-справкой и стал ВХОДОМ в панель. Рядом
              цифра непроверенных — слово владельца 03.09. */}
          <SecurityLock
            count={unverifiedCount(securityPeople)}
            expanded={securityOpen}
            lockRef={securityLockRef}
            onOpen={() => setSecurityOpen((v) => !v)}
          />
        </h2>
        {securityOpen && (
          <SecurityPanel
            people={securityPeople}
            safetyNumber={chatAlive ? (chat?.safetyNumber ?? null) : null}
            myDevice={myDevice}
            // 🔴 В комнате контроллер ждёт ПАРУ (тип, id) — ключ панели надо
            // развалить ровно так же, как это делает страница владельца.
            // Ошибка здесь молча сверяла бы код не тому человеку.
            onVerify={(key) => {
              if (view === 'room' && typeof key === 'string' && key.includes(':')) {
                const [type, id] = key.split(':');
                controllerRef.current?.markVerified(type, Number(id));
                return;
              }
              controllerRef.current?.markVerified();
            }}
            onClose={closeSecurity}
          />
        )}
        {transferOpen && (
          <DeviceTransferPanel
            onExport={runExport}
            onWipe={runWipe}
            onClose={closeTransfer}
          />
        )}
        {/* Д-17: над переключателем и вне `.msgScroll` — плашка обязана быть
            на виду и когда лента дописана до низа (автопрокрутка). */}
        <UpdateBanner />
        {/* QR-2 В2: переключатель «комната / личный чат». Показывается, только
            когда комната ЕСТЬ: при выключенном флаге участник видит ровно то
            же, что видел вчера. Одновременно живёт одна машина — переключение
            перезапускает подъём (замок один на origin).

            🔴 И только пока эта вкладка ЖИВАЯ (ревью Codex р.2, Medium).
            Вкладка, уступившая лидерство соседней, чат уже не держит и опрос
            комнаты в ней остановлен — но `hasRoom` остаётся с прошлой жизни,
            и переключатель показывал бы ЗАСТЫВШИЙ значок непрочитанного.
            Признак, который больше не обновляется, хуже отсутствующего: по
            нему нельзя судить ни о чём, а выглядит он как действующий. */}
        {/* 🔴 `repairing`/`identityLost` — в том же списке, что и остальные
            погашенные состояния (ревью р.3): иначе на экране лечения
            переключатель остаётся кликабельным и не делает ничего, а на
            «данные повреждены» — перезапускает машину на мёртвом сторе. */}
        {session && hasRoom && !yielded && !blocked && !lost && !replaced
          && !repairing && !identityLost && (
          <div className="memberViews" role="tablist" data-testid="member-views">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'room'}
              className={view === 'room' ? 'memberViewOn' : ''}
              onClick={() => { setReplyTo(null); clearHighlight(); setView('room'); }}
            >
              Общая комната
              {/* 🔴 Значок только когда человек НЕ в комнате: на открытой
                  вкладке он врал бы про уже прочитанное — курсор уезжает
                  не мгновенно, а через догон. */}
              {view !== 'room' && roomUnread > 0 && (
                <span
                  className="memberViewBadge"
                  data-testid="member-room-unread"
                  aria-label={`новых сообщений: ${roomUnread}`}
                >
                  {roomUnread > 99 ? '99+' : roomUnread}
                </span>
              )}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'direct'}
              className={view === 'direct' ? 'memberViewOn' : ''}
              onClick={() => { setReplyTo(null); clearHighlight(); setView('direct'); }}
            >
              {/* WEB-30: без имени — на телефоне вкладка обрезалась
                  («Личный чат с Александ…»), а имя и так в заголовке. */}
              Личный чат
            </button>
          </div>
        )}
        {body()}
        {/* Подпись — в ПОДВАЛЕ, не в закреплённой зоне чата: это настройка, а
            не переписка, и пиксели телефона над лентой ей не положены
            (урок WEB-13).
            🔴 `chatAlive` обязателен (ревью Codex р.2, Low). Пока подпись
            бралась из ростера комнаты, живой ростер сам был доказательством
            живой вкладки. С запасным источником из сессии кнопка «Изменить»
            вылезала бы и на погашенной вкладке (перенесён чат, доступ отозван,
            чат открыт в другой вкладке) — а PATCH там уходит в мёртвый
            транспорт и падает невнятной ошибкой. Предлагать действие, которое
            заведомо не сработает, хуже, чем не предлагать его вовсе. */}
        {/* WEB-19 §4.1 (Д-2): сервер продолжил прежнего участника этого
            устройства. Набранное имя не применялось — предлагаем применить его
            явно: молча переписать подпись было бы сюрпризом, молча выбросить —
            той самой развилкой, на которой человек шёл за дублем. */}
        {chatAlive && arrival?.resumed && !renamingSelf && (
          <div className="memberResumedNote" data-testid="member-resumed-note" role="status">
            <p className="memberSelfRenameNote">
              Вы уже входили в этот чат с этого устройства — продолжаем как
              {' '}
              <strong>{selfName ?? '…'}</strong>
              {arrival.typedName && arrival.typedName !== selfName
                ? `. Имя «${arrival.typedName}», набранное сейчас, не применялось.`
                : '.'}
            </p>
            <div className="memberSelfRenameButtons">
              <button
                type="button"
                data-testid="member-resumed-rename"
                onClick={() => {
                  setSelfDraft(arrival.typedName ?? selfName ?? '');
                  setSelfError(null);
                  setArrival(null);
                  setRenamingSelf(true);
                }}
              >
                {arrival.typedName && arrival.typedName !== selfName
                  ? `Подписаться как «${arrival.typedName}»`
                  : 'Изменить подпись'}
              </button>
              <button
                type="button"
                data-testid="member-resumed-dismiss"
                onClick={() => setArrival(null)}
              >
                Понятно
              </button>
            </div>
          </div>
        )}
        {chatAlive && selfName && !renamingSelf && (
          <p className="memberChatFooter" data-testid="member-self-name">
            {'Ваша подпись в чате: '}
            <strong>{selfName}</strong>
            {' · '}
            <button
              type="button"
              className="memberChatLeave"
              ref={selfOpenRef}
              data-testid="member-rename-open"
              onClick={openSelfRename}
            >
              Изменить
            </button>
            {/* WEB-22: остальное служебное — за этой кнопкой. `aria-expanded`
                обязателен: незрячему иначе не понять, что строка что-то
                разворачивает, а не уводит со страницы. */}
            {/* 🔴 Кнопка ТУМБЛЕР и остаётся в DOM (ревью): исчезающий
                раскрыватель уносит фокус на body, врёт своим `aria-expanded`
                (он никогда не станет `true`) и делает раскрытие
                необратимым — а на 375×470 раскрытая строка выхода стоит
                84 px, то есть 18 % экрана, ради которых срез и писался. */}
            {' · '}
            <button
              type="button"
              className="memberChatLeave"
              data-testid="member-foot-more"
              aria-expanded={footOpen}
              onClick={() => {
                setFootOpen((v) => !v);
                setLeaveAsk(false);
              }}
            >
              {footOpen ? 'Свернуть' : 'Ещё'}
            </button>
          </p>
        )}
        {chatAlive && renamingSelf && (
          <form
            className="memberSelfRename"
            data-testid="member-rename-form"
            onSubmit={submitSelfRename}
          >
            <label htmlFor="memberSelfNameInput">
              Ваша подпись в чате
              <input
                id="memberSelfNameInput"
                ref={selfInputRef}
                data-testid="member-rename-name"
                value={selfDraft}
                maxLength={64}
                onChange={(e) => setSelfDraft(e.target.value)}
              />
            </label>
            <p className="memberSelfRenameNote">
              Так вас будут видеть в комнате и в переписке — везде, включая
              уже отправленные сообщения.
            </p>
            {selfError && (
              <p className="msgChatError" data-testid="member-rename-error">{selfError}</p>
            )}
            <div className="memberSelfRenameButtons">
              <button type="submit" disabled={savingSelf} data-testid="member-rename-save">
                {savingSelf ? 'Сохраняем…' : 'Сохранить'}
              </button>
              <button
                type="button"
                disabled={savingSelf}
                data-testid="member-rename-cancel"
                onClick={closeSelfRename}
              >
                Отмена
              </button>
            </div>
          </form>
        )}
        {/* WEB-22: строка выхода видна, только когда её раскрыли, ИЛИ когда
            подписи в подвале нет вовсе (чат не поднялся, «Ещё» тогда негде
            нажать) — иначе выход стал бы недостижим. */}
        {/* 🔴 `renamingSelf` из условия убран (ревью, Low): открытая форма
            переименования показывала строку выхода, а закрытие прятало —
            скачок вёрстки на 84 px, и необратимая кнопка оказывалась в
            сантиметре от «Отмена» формы. Достижимость выхода это не трогает:
            при живом чате с подписью его открывает тумблер «Ещё», без них —
            строка рисуется сама. */}
        {/* WEB-23: второй вход в панель. Замок в шапке маленький и на телефоне
            попадает под палец не с первого раза, а «Ещё» человек уже открывает
            ради подписи и выхода. */}
        {session && chatAlive && (footOpen || !selfName) && (
          <p className="memberChatFooter">
            <button
              type="button"
              className="memberChatLeave"
              data-testid="member-foot-security"
              aria-expanded={securityOpen}
              onClick={() => setSecurityOpen((v) => !v)}
            >
              Защита переписки
            </button>
            {unverifiedCount(securityPeople) > 0
              && ` — непроверенных устройств: ${unverifiedCount(securityPeople)}.`}
          </p>
        )}
        {/* Д-3: рядом с выходом, потому что человек попадает сюда с той же
            мыслью «сменить устройство» — и обязан увидеть, что переписку можно
            унести, ДО того как нажмёт необратимый выход. */}
        {session && chatAlive && (footOpen || !selfName) && (
          <p className="memberChatFooter">
            <button
              type="button"
              className="memberChatLeave"
              data-testid="member-foot-transfer"
              ref={transferBtnRef}
              aria-expanded={transferOpen}
              onClick={() => setTransferOpen((v) => !v)}
            >
              Копия и перенос
            </button>
            {' — сохранить переписку в файл или перенести её на другое устройство.'}
          </p>
        )}
        {/* WEB-24: уведомления — выше выхода и ниже переноса. Человек, дошедший
            до подвала, чаще всего решает «как мне не пропускать сообщения»;
            выход же необратим, и он обязан оставаться последним. */}
        {session && chatAlive && (footOpen || !selfName) && pushApi && chat && (
          <PushToggle api={pushApi} variant="line" testId="member-push" />
        )}
        {session && (footOpen || !chatAlive || !selfName) && (
          <p className="memberChatFooter">
            <button
              type="button"
              className="memberChatLeave"
              data-testid="member-leave-open"
              ref={leaveOpenRef}
              aria-expanded={leaveAsk}
              onClick={askLeave}
            >
              Выйти из чата на этом устройстве
            </button>
            {' — переписка останется у собеседника, вам понадобится новая ссылка.'}
          </p>
        )}
        {/* `alertdialog` + имя от собственного текста (ревью): безымянный
            `role="group"` читалка не озвучивает вовсе, и для незрячего
            страница после нажатия «Выйти» не менялась. Не `aria-modal`:
            блок инлайновый, он ничего не перекрывает. */}
        {session && leaveAsk && (
          <div
            className="memberLeaveAsk"
            data-testid="member-leave-ask"
            role="alertdialog"
            aria-labelledby="memberLeaveAskText"
          >
            <p className="memberLeaveAskText" id="memberLeaveAskText">
              {'Переписка на этом устройстве станет недоступна '}
              <strong>навсегда</strong>
              {': ключи от неё хранятся только здесь. По ссылке вы войдёте '
                + 'новым участником — прежние сообщения не вернутся.'}
            </p>
            <div className="memberLeaveAskButtons">
              {/* 🔴 Отмена ПЕРВАЯ и обычной кнопкой: подтверждающая стоит там,
                  где палец её не найдёт случайно. Фокус приходит сюда — и
                  заодно прокручивает карточку к вопросу. */}
              <button
                type="button"
                data-testid="member-leave-cancel"
                ref={leaveCancelRef}
                onClick={() => {
                  returnLeaveFocus.current = true;
                  setLeaveAsk(false);
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                className="memberLeaveConfirm"
                data-testid="member-leave-confirm"
                onClick={leave}
              >
                Всё равно выйти
              </button>
            </div>
          </div>
        )}
        {/* 🔴 Дверь в кабинет видна ВСЕГДА, а не только без сессии (ревью
            WEB-28). Запертым оказывался ровно обратный случай: пока сессия
            участника есть, корень уводит сюда, и владельцу некуда нажать. */}
        <p className="memberChatFooter">
          <a href={CABINET_PATH} data-testid="member-cabinet-link">Войти в кабинет</a>
        </p>
        {!session && (
          <p className="memberChatFooter">
            {/* WEB-28: подвал больше не единственный выход и не зовёт «на
                страницу приглашения» — пути названы выше своими словами.
                Здесь остаётся дверь для владельца: ярлык приложения общий, и
                он мог прийти сюда по старой памяти браузера. */}
            <a href={LANDING_PATH} data-testid="member-no-session-invite">
              Открыть страницу приглашения
            </a>
          </p>
        )}
      </div>
    </main>
  );
};

export default MemberChat;
